/**
 * Per-register readers (13-16): the ONE place a page, tile or hook asks "is this reportable?",
 * "what is this risk rated?", "when is this due?" or "is it overdue?".
 *
 * Before 13-16 the SIRS subset, the rating read and the overdue cut were copied into three files
 * (`useRegisterMetrics.ts`, `RegisterTable.tsx`, `RegisterViewsPage.tsx`) and all three read
 * columns the lists don't have or no longer use (ISS-221 C1, ISS-223). Keeping them here means
 * every page shows the same count for the same rows.
 *
 * - **Reportability** keys on `ReportabilityDecision` (D-12.1-04-C). "Reportable" is exactly the
 *   live `Reportable incidents` view's filter: {SIRS-P1, SIRS-P2, NDIS-Reportable, WorkSafe}.
 *   `Pending` and a blank are UNDETERMINED, never "not reportable" (FIELDSET §1.1, QG-11).
 * - **Risk rating** is `ResidualRiskRating`, else `InherentRiskRating`, else none.
 * - **Due / overdue** use the register's own due-date column (`dueOn`, mapped per register in
 *   `config/registers.ts`), compared as Melbourne calendar days (FIELDSET §1.4,
 *   `11-QMS-GAPS.md` §5): a record due today is not overdue. Feedback has no due date.
 *
 * Pure: no React, no Graph, no I/O. Melbourne days come from `Intl` via `melbourneDay`.
 */
import { melbourneDay } from './actionVerbs';
import type {
  IncidentRecord,
  RegisterRecord,
  ReportabilityDecision,
  RiskRatingLevel,
  RiskRecord,
} from '../types/qms';

// ---------------------------------------------------------------------------
// Open
// ---------------------------------------------------------------------------

/** Statuses that close a record (excluded from open / overdue / awaiting a decision). */
const CLOSED_STATUSES: ReadonlySet<string> = new Set(['Completed', 'Cancelled']);

/** Open = `Status` ∉ {Completed, Cancelled}. */
export function isOpen(record: RegisterRecord): boolean {
  return !CLOSED_STATUSES.has(record.status);
}

// ---------------------------------------------------------------------------
// Reportability (Incidents)
// ---------------------------------------------------------------------------

/** The decisions that make an incident reportable (the live `Reportable incidents` view). */
export const REPORTABLE_DECISIONS: ReadonlySet<ReportabilityDecision> = new Set<ReportabilityDecision>([
  'SIRS-P1',
  'SIRS-P2',
  'NDIS-Reportable',
  'WorkSafe',
]);

/** What a reader may say about an incident's reportability. */
export type ReportabilityState = 'reportable' | 'not-reportable' | 'undetermined';

function decisionOf(record: RegisterRecord): string | null {
  if (record.registerKey !== 'incidents') return null;
  const d = (record as IncidentRecord).reportabilityDecision;
  return d === null || d === undefined || d.trim() === '' ? null : d.trim();
}

/**
 * The incident's reportability as recorded in `ReportabilityDecision`:
 * - SIRS-P1 / SIRS-P2 / NDIS-Reportable / WorkSafe → `reportable`;
 * - `NotReportable` → `not-reportable`;
 * - `Pending`, blank, or any value we don't recognise → `undetermined`;
 * - `Other` → `undetermined`: it is a decision recorded as Other, not a finding of
 *   reportability either way, so it stays in front of a person.
 * Other registers carry no reportability decision and read `undetermined` (they never count as
 * reportable, and callers only ask this of Incidents).
 */
export function reportabilityState(record: RegisterRecord): ReportabilityState {
  const d = decisionOf(record);
  if (d === null) return 'undetermined';
  if (REPORTABLE_DECISIONS.has(d as ReportabilityDecision)) return 'reportable';
  if (d === 'NotReportable') return 'not-reportable';
  return 'undetermined';
}

/** True only for an incident whose decision is in the reportable set. */
export function isReportable(record: RegisterRecord): boolean {
  return reportabilityState(record) === 'reportable';
}

/**
 * An open incident still waiting on a reportability decision (undetermined) — the number C3's
 * Limb B escalates on. Closed incidents are excluded.
 */
export function isAwaitingDecision(record: RegisterRecord): boolean {
  return (
    record.registerKey === 'incidents' &&
    isOpen(record) &&
    reportabilityState(record) === 'undetermined'
  );
}

// ---------------------------------------------------------------------------
// Risk rating
// ---------------------------------------------------------------------------

/** A risk's rating and which column it came from. */
export interface RiskRatingReading {
  rating: RiskRatingLevel;
  basis: 'residual' | 'inherent';
}

function rating(value: string | null | undefined): RiskRatingLevel | null {
  return value === null || value === undefined || value.trim() === ''
    ? null
    : (value.trim() as RiskRatingLevel);
}

/** `ResidualRiskRating`, else `InherentRiskRating`, else null. Null for every other register. */
export function riskRatingOf(record: RegisterRecord): RiskRatingReading | null {
  if (record.registerKey !== 'risk') return null;
  const r = record as RiskRecord;
  const residual = rating(r.residualRating);
  if (residual) return { rating: residual, basis: 'residual' };
  const inherent = rating(r.inherentRating);
  if (inherent) return { rating: inherent, basis: 'inherent' };
  return null;
}

// ---------------------------------------------------------------------------
// Due date + overdue (Melbourne days)
// ---------------------------------------------------------------------------

/**
 * The record's own due date (ISO), or null. Incidents `ResolveBy`, Complaints
 * `TargetResolutionOn`, CI `CompleteBy`, Risk `ReviewDate`; Feedback never has one.
 */
export function dueOnOf(record: RegisterRecord): string | null {
  if (record.registerKey === 'feedback') return null;
  const d = record.dueOn;
  if (d === null || d === undefined || d.trim() === '') return null;
  return Number.isFinite(Date.parse(d)) ? d : null;
}

/** Whole days from Melbourne day `a` to Melbourne day `b` (`YYYY-MM-DD` strings). */
function dayDiff(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

function todayIn(now: number | Date): string {
  return melbourneDay(new Date(now));
}

/**
 * Overdue = the record is open AND its due date's Melbourne day is BEFORE today's Melbourne
 * day. A record due today is not overdue. `now` is injected so callers stay deterministic.
 */
export function isOverdue(record: RegisterRecord, now: number | Date): boolean {
  if (!isOpen(record)) return false;
  const due = dueOnOf(record);
  if (due === null) return false;
  return melbourneDay(due) < todayIn(now);
}

/** Whole Melbourne days past due (0 when not overdue). Drives the "Nd overdue" chip. */
export function daysOverdue(record: RegisterRecord, now: number | Date): number {
  if (!isOverdue(record, now)) return 0;
  const due = dueOnOf(record) as string;
  return Math.max(0, dayDiff(melbourneDay(due), todayIn(now)));
}
